import type { CompanyAgreementWithRollup } from "@hp/types";

export interface PortfolioQuantificationStatus {
  /** Short label for table cells when no amounts exist yet. */
  shortLabel: string;
  /** Longer explanation for tooltips and detail views. */
  detail: string;
}

export function hasPortfolioClaimActivity(agreement: CompanyAgreementWithRollup): boolean {
  return agreement.flagged_claim_count > 0 || agreement.recovered_claim_count > 0;
}

/** Display amounts for open or recovered pipeline rows. */
export function portfolioDisplayAmounts(agreement: CompanyAgreementWithRollup) {
  if (agreement.flagged_claim_count > 0) {
    return {
      claimCount: agreement.flagged_claim_count,
      principal: agreement.flagged_principal_value_high_total,
      carrying: agreement.flagged_carrying_cost_value_high_total,
      total: agreement.flagged_claim_value_high_total,
      confidence: agreement.flagged_claim_avg_confidence,
      pipelineStatus: "open" as const,
    };
  }
  if (agreement.recovered_claim_count > 0) {
    return {
      claimCount: agreement.recovered_claim_count,
      principal: agreement.recovered_principal_value_high_total,
      carrying: agreement.recovered_carrying_cost_value_high_total,
      total: agreement.recovered_claim_value_high_total,
      confidence: agreement.recovered_claim_avg_confidence,
      pipelineStatus: "recovered" as const,
    };
  }
  return null;
}

/** Why a portfolio-scan row has no confidence or rupee amounts yet. */
export function getPortfolioQuantificationStatus(
  agreement: CompanyAgreementWithRollup,
): PortfolioQuantificationStatus | null {
  if (hasPortfolioClaimActivity(agreement)) {
    return null;
  }

  switch (agreement.processing_status) {
    case "needs_ocr":
      return {
        shortLabel: "Awaiting OCR",
        detail:
          "Only the PDF is on file. Counterparty, clauses, and tariff data were not extracted yet, so claims cannot be quantified.",
      };
    case "extracted":
      return {
        shortLabel: "Needs tariff ledger",
        detail:
          "Clauses were extracted but no tariff line items are linked. Add ledger data under Workbench before the engine can price potential claims.",
      };
    case "ledger_ready":
      return {
        shortLabel: "No claims on file",
        detail:
          "Ledger is linked but no priced claim candidates are matched to this contract right now.",
      };
    case "placeholder":
    default:
      return {
        shortLabel: "Not quantified",
        detail:
          "This contract is on file but has no extracted commercial terms or ledger rows to match regulatory findings against.",
      };
  }
}
